import React from "react";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { Box } from "@mui/system";
// import RebootButton from "./RebootButton";

const toTitleCase = (str) =>
  str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );

const formatStateMachine = (str) => toTitleCase(str).replace("_", " ");

const formatDeviceLabel = ({ displayName, stateMachine }) => {
  const deviceStateLabel = stateMachine
    ? `[${formatStateMachine(stateMachine)}]`
    : "[Off]";
  return `${toTitleCase(displayName)} ${
    displayName?.toLowerCase?.() !== "all devices" ? deviceStateLabel : ""
  }`;
};

// menu order by state - anything not listed sits between UPDATING and OFF, and
// a device with no state is shown as Off so it sorts with OFF
const STATE_MACHINE_RANK = {
  IDLE: 0,
  BOOTING: 1,
  UPDATING: 2,
  ALG_UPDATING: 2,
  OFF: 4,
};
const OTHER_STATE_RANK = 3;

const stateMachineRank = (stateMachine) =>
  stateMachine
    ? STATE_MACHINE_RANK[stateMachine.toUpperCase()] ?? OTHER_STATE_RANK
    : STATE_MACHINE_RANK.OFF;

const compareText = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    sensitivity: "base",
  });

// the "all devices" entry stays pinned at the bottom, where it has always been
export const sortDevices = (devices) =>
  Object.entries(devices).sort(
    ([uuidA, a], [uuidB, b]) =>
      (uuidA === "ALLTRAY") - (uuidB === "ALLTRAY") ||
      stateMachineRank(a?.stateMachine) - stateMachineRank(b?.stateMachine) ||
      compareText(a?.displayName, b?.displayName) ||
      compareText(uuidA, uuidB)
  );

const DeviceSelector = ({
  devices,
  selectedDevice,
  handleChangeDevice,
  loading,
  disabled,
}) => {
  const deviceList = sortDevices(devices).map(([uuid, device]) => (
    <MenuItem
      value={uuid}
      key={uuid}
      className={"w-full text-center"}
      sx={{
        fontSize: [".75rem", ".75rem", ".75rem", ".75rem", "1rem", "1rem"],
      }}
    >
      {formatDeviceLabel(device)}
    </MenuItem>
  ));

  // the closed bar falls back to a placeholder so an unselected dropdown does
  // not read as an empty white bar
  const renderSelectedDevice = (uuid) =>
    devices?.[uuid] ? (
      formatDeviceLabel(devices[uuid])
    ) : (
      <span className={"text-gray-600"}>Select device</span>
    );

  return (
    <Box
      className={"flex top-0 z-40"}
      id={"device-selector"}
      aria-label="device-select-dropdown"
      sx={{
        position: "absolute",
        left: "50%",
        transform: "translate(-50%, 0)",
      }}
    >
      <FormControl
        variant="standard"
        sx={{
          width: ["10rem", "10rem", "16rem", "16rem", "20rem", "24rem"],
        }}
      >
        <Select
          className={"bg-white shadow-xl flex-1 top-30 text-center first:p-0"}
          label="Select Device"
          id="idtool-select"
          value={Object.values(devices).length > 0 ? selectedDevice : ''}
          disabled={disabled}
          onChange={handleChangeDevice}
          displayEmpty
          renderValue={renderSelectedDevice}
          sx={{
            fontSize: [".75rem", ".75rem", ".75rem", ".75rem", "1rem", "1rem"],
          }}
        >
          <MenuItem
            sx={{
              height: { sx: "1.85rem", lg: "2.1rem" }
            }}
            value={"none"}
            key={"1"}
          >
            <p></p>
          </MenuItem>
          {deviceList}
        </Select>
        <Box
          sx={{
            position: "fixed",
            right: "-10%",
          }}
        >
          {loading ? (
            <CircularProgress
              id="device-selector-loading-circle"
              sx={{
                cx: [0, 0, 0, 0, 0, 0],
                cy: [0, 0, 0, 0, 0, 0],
                width: { sx: "25px !important", lg: "30px !important" },
                height: { sx: "25px !important", lg: "30px !important" },
              }}
            />
          ) : null}
        </Box>
      </FormControl>
      {/* <RebootButton /> */}
    </Box>
  );
};

export default DeviceSelector;
